import clsx from 'clsx';
import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import uiConfig from '../../data/uiConfig';

interface TabItem {
  label?: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  defaultTab?: number;
  minHeight?: number;
  bgColor?: string;
  color?: string;
}

// Context pour permettre la navigation depuis le contenu
interface TabsContextType {
  switchToTab: (index: number) => void;
}

const TabsContext = createContext<TabsContextType | null>(null);

// Hook pour utiliser le contexte dans les composants enfants
export const useTabsNavigation = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabsNavigation must be used within a Tabs component');
  }
  return context;
};

export const Tabs: React.FC<TabsProps> = ({ 
  tabs, 
  defaultTab = 0, 
  minHeight,
  bgColor = uiConfig.tabs.selected,
  color = uiConfig.tabs.selectedText
 }) => {
  const [active, setActive] = useState(defaultTab);
  const [left, setLeft] = useState(0);
  const [width, setWidth] = useState(0);
  const [fade, setFade] = useState(true);

  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const handleTabChange = (index: number) => {
    setFade(false);
    setTimeout(() => {
      setActive(index);
      setFade(true);
      // Scroll vers le haut du contenu après le changement d'onglet
      setTimeout(() => {
        const topOfPage = document.getElementById(`root`)?.getBoundingClientRect().top;
        if (topOfPage) {
          window.scrollTo({ top: topOfPage, behavior: 'smooth' });
        }
      }, 200); // Attendre que l'animation soit terminée
    }, 150); // Durée de la sortie
  };

  // Fonction pour changer d'onglet depuis le contenu
  const switchToTab = (index: number) => {
    if (index >= 0 && index < tabs.length) {
      handleTabChange(index);
    }
  };


  useEffect(() => {
    const curr = tabsRef.current[active];
    if (curr) {
      setLeft(curr.offsetLeft);
      setWidth(curr.clientWidth);
    }
    const handleResize = () => {
      const cur = tabsRef.current[active];
      if (cur) {
        setLeft(cur.offsetLeft);
        setWidth(cur.clientWidth);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [active]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      setActive((prev) => (prev + 1) % tabs.length);
    } else if (e.key === 'ArrowLeft') {
      setActive((prev) => (prev - 1 + tabs.length) % tabs.length);
    }
  };

  return (
    <TabsContext.Provider value={{ switchToTab }}>
      <div>
        <div className="relative">
        <div
          className="flex border-b border-gray-300 mb-2 md:mb-0 gap-2 md:gap-0 pb-2 md:pb-0 px-2 md:px-0 overflow-x-auto"
          role="tablist"
          aria-label="Contenu des onglets"
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {tabs.map((tab, i) => (
            <button
              className={clsx(
                "flex flex-1 flex-row md:flex-col items-center justify-center px-4 py-2 text-sm leading-none md:text-base md:uppercase text-center",
                "rounded-lg md:rounded-none transition-all duration-300",
                "bg-gray-100 md:bg-transparent",
                active === i 
                  ? `${color} font-semibold border-2 border-primary md:border-0` 
                  : "text-gray-600 border-2 border-transparent md:border-0",
              )}
              onClick={() => handleTabChange(i)}
              ref={(el) => { tabsRef.current[i] = el }}
              key={i}
              role="tab"
              aria-selected={active === i}
              aria-controls={`tabpanel-${i}`}
              id={`tab-${i}`}
              tabIndex={active === i ? 0 : -1}
            >
              {tab.icon && (
                <span className={clsx(
                  "mr-2 md:mr-0 md:mb-1",
                  active === i ? "text-primary" : "text-gray-500"
                )}>
                  {tab.icon}
                </span>
              )}
              {tab.label}
            </button>
          ))}
        </div>
        {/* Ligne indicateur pour desktop uniquement */}
        <span
          className={`hidden md:block absolute bottom-0 h-[3px] ${bgColor} transition-all duration-300 rounded-full`}
          style={{ left, width }}
        />
        {/* Ligne indicateur pour mobile uniquement */}
        <span
          className={`md:hidden absolute bottom-0 h-[2px] bg-gray-300 transition-all duration-300`}
          style={{ left, width }}
        />
      </div>
      <div
        className={clsx(
          "p-4 transition-all duration-300 ease-in-out",
          fade ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )} style={{
          minHeight: minHeight ?? undefined
        }}
        role="tabpanel"
        id={`tabpanel-${active}`}
        aria-labelledby={`tab-${active}`}
      >
        {tabs[active].content}
      </div>
    </div>
    </TabsContext.Provider>
  );

};